import { useCallback, useEffect, useMemo, useRef } from 'react';
import { getBetaAvailabilityKey, normalizeBetaSource } from '../../../utils/betaSources';
import { fetchCurrentUserData } from '../../../utils/ApiCall';
import { isReadyWatchlistPublicationRefreshResult } from '../dashboardUtils';
import { getRiskCommandsOrThrow } from '../orchestration/riskCommandRef';
import { RISK_INVALIDATION_CAUSES } from '../orchestration/useRiskOrchestrationCommands';
import { createPublicationStateTransaction } from '../orchestration/publicationStateTransaction';
import {
    parseWatchlistPublicationReceipt,
    WATCHLIST_PUBLICATION_DISPOSITIONS,
} from '../../../utils/watchlistPublication';
import {
    parseWatchlistDeltaClassification,
    WATCHLIST_DELTA_CLASSIFICATIONS,
    WATCHLIST_CLIENT_ACTUAL_SYNC_APPLIED_FIELD,
    requiresWatchlistClientPublication,
} from '../../../utils/securityResolutionEvents';

const RETRYABLE_PUBLICATION_REASONS = new Set([
    'market_data_pending',
    'watchlist_rows_not_ready',
]);
export const PUBLICATION_RETRY_DELAYS_MS = Object.freeze([200, 400, 800, 1200]);

const firstNonblankValue = (...values) => {
    for (const value of values) {
        const normalized = String(value ?? '').trim();
        if (normalized) return normalized;
    }
    return null;
};

const wait = (delayMs) => new Promise((resolve) => {
    window.setTimeout(resolve, delayMs);
});

function buildPublicationReadinessError(loadResult) {
    const matrixStatus = String(loadResult?.matrixStatus || 'unavailable').trim().toLowerCase();
    const watchlistSurfaceSource = String(
        loadResult?.watchlistSurface?.source || '',
    ).trim().toLowerCase();
    const reason = String(
        loadResult?.matrixReason
        || (watchlistSurfaceSource === 'not_loaded' ? 'watchlist_rows_not_ready' : ''),
    ).trim().toLowerCase();
    const error = new Error(
        reason === 'serving_manifest_context_unavailable'
            ? 'Factor data is not available for the current Watchlist.'
            : `Watchlist publication refresh is not ready (${matrixStatus}).`,
    );
    error.code = reason || `matrix_${matrixStatus}`;
    error.retryable = RETRYABLE_PUBLICATION_REASONS.has(error.code)
        && (matrixStatus === 'pending' || error.code === 'watchlist_rows_not_ready');
    return error;
}

function parseDurablePublicationPreferences(userData) {
    const availability = userData?.available_beta_sources;
    if (!availability || typeof availability !== 'object' || Array.isArray(availability)) {
        throw new Error('Current beta-source availability is invalid.');
    }
    const betaSource = String(userData?.beta_source ?? '').trim().toLowerCase();
    const recognizedBetaSource = (
        betaSource === 'yahoo'
        || betaSource === 'factor'
        || (
            betaSource.startsWith('watchlist:')
            && betaSource.slice('watchlist:'.length).trim().length > 0
        )
    );
    const availabilityEntry = availability[betaSource];
    if (
        !recognizedBetaSource
        || !availabilityEntry
        || typeof availabilityEntry !== 'object'
        || Array.isArray(availabilityEntry)
        || availabilityEntry.available !== true
    ) {
        throw new Error('The durable beta source is unavailable.');
    }
    const missingFactorPolicy = userData?.missing_factor_policy;
    if (missingFactorPolicy !== 'ignore' && missingFactorPolicy !== 'impute') {
        throw new Error('The durable missing-factor policy is invalid.');
    }
    return {
        availability,
        betaSource,
        missingFactorPolicy,
    };
}

// The saved factor setting as the server reads it after a publication. A
// Settings vendor switch saves its vendor only when it publishes.
const DURABLE_FACTOR_SETTING_FIELDS = ['factor_model', 'axioma_model', 'barra_model', 'idio_vol_source'];

export function durableFactorSetting(userData) {
    return Object.fromEntries(DURABLE_FACTOR_SETTING_FIELDS
        .map((field) => [field, String(userData?.[field] ?? '').trim()])
        .filter(([, value]) => value));
}

// The factor data date the published revision pins. The server serves only
// that date for the active Watchlist, and a factor data update moves it past
// the date on screen, so the publication's refresh asks for this one.
function publishedFactorSnapshotDate(publication) {
    const date = String(
        publication?.factor_context?.factor_as_of_date
        ?? publication?.lifecycle?.factor_context?.factor_as_of_date
        ?? '',
    ).trim();
    return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : undefined;
}

export default function useWatchlistWorkflow({
    userUid,
    requestPosSizingContextUpdate,
    activeView,
    setupGuideDismissed,
    watchlistIsReady,
    watchlistLoading = false,
    actualPortfolioIsReady,
    hasPosSizingData,
    watchlistGuideAcknowledged,
    setWatchlistGuideAcknowledged,
    portfolioGuideAcknowledged,
    setPortfolioGuideAcknowledged,
    updateSetupGuideState,
    onUserDataChange,
    currentBetaSource = null,
    beginPublicationVersionTransition,
    preparePublicationTarget,
    prepareMountedPublication = null,
    riskCommandsRef,
    markPendingRefreshFromLoadResult = null,
    onMandatoryFactorContextLoaded = null,
    onPublicationCoreLoadCommitted = null,
    fetchDurableUserData = fetchCurrentUserData,
    publishedBetaContextRef,
}) {
    const routePublicationGenerationRef = useRef(0);
    const setWatchlistGuideAcknowledgedRef = useRef(setWatchlistGuideAcknowledged);
    setWatchlistGuideAcknowledgedRef.current = setWatchlistGuideAcknowledged;
    const hasWatchlistData = Boolean(watchlistIsReady);
    const hasPortfolioData = Boolean(actualPortfolioIsReady);

    const setupGuideStep = useMemo(() => {
        if (watchlistLoading || watchlistIsReady == null) return null;
        // Empty membership needs an entry point even after an old setup dismissal.
        if (watchlistIsReady === false) return 'watchlist';
        if (setupGuideDismissed) return null;
        if (actualPortfolioIsReady == null) return null;
        if (!hasPortfolioData) return 'portfolio';
        return null;
    }, [
        actualPortfolioIsReady,
        hasPortfolioData,
        hasWatchlistData,
        setupGuideDismissed,
        watchlistIsReady,
        watchlistLoading,
    ]);

    const setupGuideTargetTab = setupGuideStep === 'watchlist'
        ? 'watchlist'
        : setupGuideStep === 'portfolio'
            ? 'overview'
            : null;

    const showWatchlistUploadGuide = (
        setupGuideStep === 'watchlist' &&
        activeView === 'watchlist' &&
        !watchlistGuideAcknowledged
    );
    const showPortfolioImportGuide = (
        setupGuideStep === 'portfolio' &&
        activeView === 'overview' &&
        !portfolioGuideAcknowledged
    );

    useEffect(() => {
        routePublicationGenerationRef.current += 1;
        setWatchlistGuideAcknowledgedRef.current(false);
        return () => {
            routePublicationGenerationRef.current += 1;
        };
    }, [userUid]);

    useEffect(() => {
        if (watchlistIsReady === true && watchlistGuideAcknowledged) {
            setWatchlistGuideAcknowledged(false);
        }
        updateSetupGuideState({
            step: setupGuideStep,
            targetTab: setupGuideTargetTab,
            hasWatchlist: hasWatchlistData,
            hasPortfolio: hasPortfolioData,
            hasPosSizing: hasPosSizingData,
            watchlistUploadInteracted: watchlistGuideAcknowledged,
            portfolioImportInteracted: portfolioGuideAcknowledged,
        });
    }, [
        hasPortfolioData,
        hasPosSizingData,
        hasWatchlistData,
        portfolioGuideAcknowledged,
        setupGuideStep,
        setupGuideTargetTab,
        updateSetupGuideState,
        watchlistGuideAcknowledged,
        watchlistIsReady,
        setWatchlistGuideAcknowledged,
    ]);

    const handleWatchlistGuideInteraction = useCallback(() => {
        setWatchlistGuideAcknowledged(true);
    }, [setWatchlistGuideAcknowledged]);

    const handlePortfolioGuideInteraction = useCallback(() => {
        setPortfolioGuideAcknowledged(true);
    }, [setPortfolioGuideAcknowledged]);

    const publishWatchlistRevision = useCallback(async (
        publication = {},
        executionAuthority = null,
    ) => {
        const { reason = 'watchlist_upload' } = publication;
        const classificationResult = parseWatchlistDeltaClassification(publication);
        const requiresClientPublication = requiresWatchlistClientPublication(publication);
        if (
            classificationResult.error
            || (
                classificationResult.classification
                    !== WATCHLIST_DELTA_CLASSIFICATIONS.MEMBERSHIP_CHANGED
                && !requiresClientPublication
            )
        ) {
            throw new Error(
                classificationResult.error
                || 'Mandatory publication requires membership_changed.',
            );
        }
        const publicationReceipt = parseWatchlistPublicationReceipt(publication);
        if (!publicationReceipt) {
            throw new Error(
                'Terminal watchlist publication requires a revision id and positive revision sequence.',
            );
        }
        const executionOwner = executionAuthority?.owner ?? null;
        if (
            !executionOwner
            || typeof executionAuthority?.isCurrent !== 'function'
        ) {
            throw new Error('Mandatory publication requires exact coordinator authority.');
        }
        const routeGeneration = routePublicationGenerationRef.current;
        const executionIsCurrent = () => (
            routePublicationGenerationRef.current === routeGeneration
            && executionAuthority.isCurrent()
        );
        let publicationVersionTransition = null;
        const presentation = createPublicationStateTransaction();
        const participants = [];
        let preparedTransferred = false;
        let preparedTarget = null;
        const discard = () => {
            presentation.discard();
            participants.forEach(participant => participant.discard());
            preparedTarget?.discard?.();
        };
        const isLatestPublication = () => executionIsCurrent()
            && (!publicationVersionTransition || publicationVersionTransition.isCurrent());
        try {
        if (!isLatestPublication()) {
            return { disposition: WATCHLIST_PUBLICATION_DISPOSITIONS.OBSOLETE };
        }

        const durableUserData = await fetchDurableUserData();
        if (!isLatestPublication()) {
            return { disposition: WATCHLIST_PUBLICATION_DISPOSITIONS.OBSOLETE };
        }
        const {
            availability: durableAvailability,
            betaSource: durableBetaSource,
            missingFactorPolicy: durablePolicy,
        } = parseDurablePublicationPreferences(durableUserData);
        const betaContextChanged = (
            normalizeBetaSource(currentBetaSource) !== durableBetaSource
        );
        const commands = getRiskCommandsOrThrow(
            riskCommandsRef,
            'useWatchlistWorkflow.handleWatchlistUploadSuccess',
            ['invalidateRiskSurfaces', 'refreshRiskSurface'],
        );
        const invalidations = betaContextChanged
            ? [{
                cause: RISK_INVALIDATION_CAUSES.BETA_SOURCE_CHANGED,
                reason: `${reason}:durable_beta_context_changed`,
            }]
            : [];
        invalidations.push({
            cause: RISK_INVALIDATION_CAUSES.WATCHLIST_MEMBERSHIP_CHANGED,
            reason,
        });
        commands.invalidateRiskSurfaces(invalidations, { preserveVisibleState: true });
        if (!isLatestPublication()) {
            return { disposition: WATCHLIST_PUBLICATION_DISPOSITIONS.OBSOLETE };
        }

        const securityOperationId = firstNonblankValue(
            publication?.operation_id,
            publication?.lifecycle?.operation_id,
        );
        const securityOperationType = firstNonblankValue(
            publication?.operation_type,
            publication?.lifecycle?.operation_type,
        );
        const publishedFactor = publication?.factor_context ?? publication?.lifecycle?.factor_context;
        const publishedVendor = publishedFactor?.provider_vendor ?? durableUserData?.factor_model;
        const publishedModel = publishedFactor?.factor_model;
        const publishedHorizon = publishedFactor?.factor_horizon;
        const publishedDataModel = publishedModel
            ? (publishedVendor === 'axioma' && publishedHorizon && !publishedModel.endsWith(`-${publishedHorizon}`)
                ? `${publishedModel}-${publishedHorizon}` : publishedModel)
            : durableUserData?.[`${publishedVendor}_model`];
        const terminalReceipt = publication?.terminal_receipt ?? publication?.lifecycle?.terminal_receipt ?? null;
        const portfolioSyncApplied = terminalReceipt?.portfolio_sync?.applied === true
            || publication?.[WATCHLIST_CLIENT_ACTUAL_SYNC_APPLIED_FIELD] === true;
        if (typeof beginPublicationVersionTransition !== 'function') {
            throw new Error('Mandatory publication requires an owned resource version transition.');
        }
        publicationVersionTransition = beginPublicationVersionTransition({
            actualSyncApplied: portfolioSyncApplied, isCurrent: executionIsCurrent,
        });
        const loadOptions = {
            reason,
            publicationVersionTransition,
            preparePublication: true,
            deferSelectedOverview: true,
            stagePresentationState: presentation.stage,
            factorVendorOverride: publishedVendor,
            factorDataModelOverride: publishedDataModel,
            betaSource: durableBetaSource,
            ...(durableUserData?.idio_vol_source ? { idioVolSource: durableUserData.idio_vol_source } : {}),
            requireFreshMatrices: true,
            matrixScopeVersion: (
                `revision:${publicationReceipt.revisionSeq}:${publicationReceipt.revisionId}`
            ),
            factorSnapshotDateOverride: publishedFactorSnapshotDate(publication),
            includeSelectedOverview: activeView === 'overview',
            includeOptimizerSummaries: false,
            deferSecondary: true,
            preserveExistingOnMatrixFailure: true,
            securityOperationId,
            securityOperationType,
            expectedWatchlistRevisionId: publicationReceipt.revisionId,
            expectedWatchlistRevisionSeq: publicationReceipt.revisionSeq,
            exclusiveCoreLoadOwner: executionOwner,
        };
        let loadResult = null;
        for (let attempt = 0; ; attempt += 1) {
            try {
                loadResult = await commands.refreshRiskSurface(loadOptions);
                if (!isLatestPublication()) {
                    loadResult?.prepared?.discard?.();
                    return { disposition: WATCHLIST_PUBLICATION_DISPOSITIONS.OBSOLETE };
                }
                if (!isReadyWatchlistPublicationRefreshResult(loadResult)) {
                    throw buildPublicationReadinessError(loadResult);
                }
                break;
            } catch (error) {
                loadResult?.prepared?.discard?.();
                loadResult = null;
                const retryDelay = PUBLICATION_RETRY_DELAYS_MS[attempt];
                if (error?.retryable !== true || retryDelay == null) {
                    throw error;
                }
                if (!isLatestPublication()) {
                    return { disposition: WATCHLIST_PUBLICATION_DISPOSITIONS.OBSOLETE };
                }
                await wait(retryDelay);
                if (!isLatestPublication()) {
                    return { disposition: WATCHLIST_PUBLICATION_DISPOSITIONS.OBSOLETE };
                }
            }
        }
        if (!isLatestPublication()) {
            loadResult?.prepared?.discard?.();
            return { disposition: WATCHLIST_PUBLICATION_DISPOSITIONS.OBSOLETE };
        }

        const corePrepared = loadResult?.prepared;
        if (!corePrepared || ['canCommit', 'commit', 'discard'].some(name => typeof corePrepared[name] !== 'function')
            || !corePrepared.canCommit()) {
            throw new Error('Mandatory publication did not prepare its guarded Core state.');
        }
        if (!participants.includes(corePrepared)) participants.push(corePrepared);
        if (typeof preparePublicationTarget !== 'function') {
            throw new Error('Mandatory publication requires explicit prepared target inputs.');
        }
        preparedTarget = await preparePublicationTarget({
            loadResult, publication, durableUserData, publicationVersionTransition, actualSyncApplied: portfolioSyncApplied,
            stagePublicationState: presentation.stage,
            isCurrentRequest: isLatestPublication,
        });
        if (!isLatestPublication()) return { disposition: WATCHLIST_PUBLICATION_DISPOSITIONS.OBSOLETE };
        if (!preparedTarget) return { disposition: WATCHLIST_PUBLICATION_DISPOSITIONS.OBSOLETE };
        if (typeof preparedTarget.canCommit !== 'function') {
            throw new Error('Prepared publication target requires an exact currentness guard.');
        }
        if (!preparedTarget.factorContext && loadResult.matrixStatus !== 'empty_watchlist') {
            throw new Error('Prepared publication target factor context is missing.');
        }
        const authoritativeEmpty = loadResult.matrixStatus === 'empty_watchlist';
        if (!authoritativeEmpty && (
            typeof onMandatoryFactorContextLoaded !== 'function'
            || onMandatoryFactorContextLoaded(loadResult, reason, {
                stagePublicationState: presentation.stage, isCurrentRequest: isLatestPublication,
            }) !== true
        )) throw new Error('Mandatory factor context promotion failed.');
        presentation.stage(() => markPendingRefreshFromLoadResult?.(loadResult), isLatestPublication);
        const publishedBetaContext = betaContextChanged ? {
            betaSource: durableBetaSource, availabilityKey: getBetaAvailabilityKey(durableAvailability),
        } : null;
        const durableUserPatch = {
            available_beta_sources: durableAvailability,
            has_user_defined_betas: Object.keys(durableAvailability).some(sourceId => sourceId.startsWith('watchlist:')),
            beta_source: durableBetaSource,
            missing_factor_policy: durablePolicy,
            ...durableFactorSetting(durableUserData),
            watchlist_version_count: durableUserData?.watchlist_version_count ?? null,
        };
        presentation.stage(() => {
            if (publishedBetaContext && publishedBetaContextRef) publishedBetaContextRef.current = publishedBetaContext;
            onUserDataChange?.(durableUserPatch);
        }, isLatestPublication);
        const childUpdate = await requestPosSizingContextUpdate({
            reason, acceptance: portfolioSyncApplied ? 'actual_change' : 'automatic',
            allowParentPublication: true,
            matricesOverride: loadResult.matricesOverride ?? null,
            preparedTarget,
            stagePublicationState: presentation.stage,
            isCurrentRequest: isLatestPublication,
        });
        if (!isLatestPublication()) return { disposition: WATCHLIST_PUBLICATION_DISPOSITIONS.OBSOLETE };
        if (childUpdate?.success !== true) {
            if (childUpdate?.error instanceof Error) throw childUpdate.error;
            throw new Error(childUpdate?.message || 'Watchlist was published, but Position Sizing still needs to be synced.');
        }
        let optimizerSummaries = childUpdate?.optimizerSummaries ?? null;
        if (!optimizerSummaries) {
            optimizerSummaries = authoritativeEmpty ? { status: 'skipped', reason: 'empty_watchlist' }
                : await getRiskCommandsOrThrow(riskCommandsRef, 'useWatchlistWorkflow.prepareSummaries',
                    ['refreshOptimizerSummaries']).refreshOptimizerSummaries({
                    stagePublicationState: presentation.stage, preparedTarget, isCurrentRequest: isLatestPublication,
                });
        }
        if (!isLatestPublication()) return { disposition: WATCHLIST_PUBLICATION_DISPOSITIONS.OBSOLETE };
        if (!Array.isArray(optimizerSummaries?.portfolios)
            && !(authoritativeEmpty && optimizerSummaries?.status === 'skipped' && optimizerSummaries?.reason === 'empty_watchlist')) {
            throw new Error('The saved portfolio list could not be prepared for the final reveal.');
        }
        preparedTarget = {
            ...preparedTarget,
            versions: { ...preparedTarget.versions },
            optimizerSummaries,
            workspace: childUpdate?.workspace ?? null,
            childDecision: childUpdate,
        };
        // The selected scope is computed only after the child's planned version
        // is known; no second visible helper load follows the final reveal.
        if (typeof preparedTarget.prepareSelectedOverview === 'function') {
            await preparedTarget.prepareSelectedOverview(loadResult, preparedTarget);
        }
        if (!isLatestPublication()) return { disposition: WATCHLIST_PUBLICATION_DISPOSITIONS.OBSOLETE };
        if (typeof prepareMountedPublication === 'function') {
            const mounted = await prepareMountedPublication({
                preparedTarget, isCurrentRequest: isLatestPublication,
                stagePublicationState: presentation.stage,
            });
            if (mounted) {
                if (['canCommit', 'commit', 'discard'].some(name => typeof mounted[name] !== 'function')) {
                    throw new Error('Mounted publication preparation returned an invalid handle.');
                }
                participants.push(mounted);
                presentation.stage(mounted.commit, mounted.canCommit);
            }
        }
        if (!isLatestPublication()) return { disposition: WATCHLIST_PUBLICATION_DISPOSITIONS.OBSOLETE };
        if (typeof corePrepared.transferTo !== 'function' || !corePrepared.transferTo(presentation.stage)) {
            throw new Error('Prepared Core state could not join the final publication boundary.');
        }
        presentation.stage(() => onPublicationCoreLoadCommitted?.(loadResult, preparedTarget), isLatestPublication);
        const canCommit = () => isLatestPublication()
            && preparedTarget.canCommit()
            && participants.every(participant => participant.canCommit())
            && presentation.canCommit(isLatestPublication);
        if (!canCommit()) return { disposition: WATCHLIST_PUBLICATION_DISPOSITIONS.OBSOLETE };
        preparedTransferred = true;
        return {
            disposition: WATCHLIST_PUBLICATION_DISPOSITIONS.PREPARED,
            prepared: { canCommit, commit: () => {
                if (!canCommit()) { discard(); return false; }
                return presentation.commit(canCommit);
            }, discard },
            loadResult,
        };
        } catch (error) {
            if (error?.code === 'performance_preparation_obsolete') {
                return { disposition: WATCHLIST_PUBLICATION_DISPOSITIONS.OBSOLETE };
            }
            throw error;
        } finally {
            if (!preparedTransferred) discard();
        }
    }, [
        activeView,
        requestPosSizingContextUpdate,
        beginPublicationVersionTransition,
        preparePublicationTarget,
        prepareMountedPublication,
        currentBetaSource,
        fetchDurableUserData,
        markPendingRefreshFromLoadResult,
        onMandatoryFactorContextLoaded,
        onPublicationCoreLoadCommitted,
        onUserDataChange,
        publishedBetaContextRef,
        riskCommandsRef,
    ]);

    const handleWatchlistUploadSuccess = publishWatchlistRevision;

    return {
        showWatchlistUploadGuide,
        showPortfolioImportGuide,
        handleWatchlistGuideInteraction,
        handlePortfolioGuideInteraction,
        handleWatchlistUploadSuccess,
    };
}
